module Meevo.Plugins.SalesRankings {

    import SaleRankFilterTypeEnum = Meevo.Model.SaleRankFilterTypeEnum;

    interface ISalesRankingsService {
        getCurrencySymbol: () => string;
        processItem: (list: any, criteria: IPreviewCriteria) => void;
        compileFilterType: (criteria: IPreviewCriteria) => number;
        processCSVRow: (row: IItem, criteria: IPreviewCriteria) => string;
        getTableView: (criteria: IPreviewCriteria) => ITableView;
        detailedItemsList: (list: any) => Array<any>;
    }

    interface ITableView {
        tableColumns: ITableColumnTitle,
        productTitle: string,
        serviceTitle: string,
        productServiceTitle: string,
        tableTitle: string,
        retailToServiceTitle: string,
        retailToTotalTitle: string,
        clientsRebookedTitle: string,
        rebookedTitle: string
    }

    export interface ITableColumnTitle {
        products: boolean,
        services: boolean,
        productsServices: boolean,
        retailToService: boolean,
        retailToTotal: boolean,
        rebooking: boolean
    }

    export interface IItem {
        index: number,
        displayName: string,
        quantity: number,
        sumValue?: number,
        productValue?: number,
        serviceValue?: number,
        value?: number,
        id?: string,
        itemName?: string,
        amount?: number,
        totalValue?: number,
        totalQuantity? :number,


        // client info for CSV
        numericIdentifier?: string,
        prefix?: string,
        firstName?: string,
        middleName?: string,
        lastName?: string,
        address1?: string,
        address2?: string,
        city?: string,
        state?: string,
        zip?: string,
        homePhoneNumber?: string,
        mobilePhoneNumber?: string,
        apptNotifOptInState?: string,
        useForTextMarketing?: string,
        workPhoneNumber?: string,
        workPhoneExtension?: string,
        primaryPhoneType?: string,
        emailAddress?: string,
        emailStatus?: string,
        emailCommOptedInState?: string,
        isMarketingEmailEnabled?: string,
        firstVisitDate?: string
        lastVisitDate?: string,
        gender?: string,
        birthYear?: string,
        birthMonth?: string,
        birthDay?: string,
        anniversaryDate?: string,
        clientType?: string,
        isMinor?: string,
        isRestricted?: string,
        isTransient?: string,
        allowBookingRebookingOnline?: string,
        excludeFromReceivingPoints?: string,
        allowChargesCreditOnAccount?: string,
        isMember?: string,
        referralType?: string,
        referralTypeDisplayName?: string,
        referralFirstName?: string,
        referralLastName?: string,
        noSellSharePersonalInfo?: string,

        // employee info for CSV
        employeeCode?: string,
        employeeId?: string,

        // additional properties
        isExpanded?: boolean,
        expandedItemsList?: any
    }

    export interface ICriteria {
        criteria: {
            saleRankFilterType: any,
            startDate: Date,
            endDate: Date,
            isPercent: boolean,
            topCount: number,
            advancedFilter?: IAdvancedFilter,
            clientId?: string,
            employeeFilterList?: any
        },
        sortDirection?: number,
        sortBy?: string,
        employeeId?: string,
        employeeIdList?: Array<any>,
        isForExport?: boolean
    }

    export interface IAdvancedFilter {
        serviceCriteria?: {
            serviceId?: any,
            serviceCategoryId?: any,
            serviceSubCategoryId?: any
        },
        productCriteria?: {
            productId?: any,
            productCategoryId?: any,
            productSubCategoryId?: any,
            manufacturerId?: any,
            distributorId?: any
        },
        giftCardCriteria?: {
            giftCardTypeId?: any
        },
        membershipCriteria?: {
            membershipTypeId?: any,
            membershipId?: any
        },
        packageCriteria?: {
            packageId?: any
        }
    }

    export interface IAdvancedSearchCriteria {
        service?: any,
        serviceCategory?: any,
        serviceSubCategory?: any,
        product?: any,
        productCategory?: any,
        productSubCategory?: any,
        manufacturer?: any,
        distributor?: any,
        giftCardType?: any,
        membershipType?: any,
        membership?: any,
        package?: any
    }

    export interface IPreviewCriteria {
        showFilter: number,
        itemFilter: number,
        itemsPercentageFilter: number
    }

    export enum ShowFilterEnum {
        All,
        ClientsPurchased,
        EmployeesSold
    }

    export enum ItemFilterEnum {
        GiftCards,
        Memberships,
        Packages,
        Products,
        Services,
        ProductsServices,
        AverageTicket,
        Rebooking,
        RetailToService,
        RetailToTotal
    }

    export enum ItemsPercentageEnum {
        Items,
        Percentage
    }

    export enum SaleRankDetailType {
        GiftCards,
        Memberships,
        Packages,
        Products,
        Services
    }

    export class SalesRankingsService implements ISalesRankingsService {

        static $inject = [
            '$translate',
            '$locale'
        ];

        constructor(
            protected $translate: angular.translate.ITranslateService,
            protected $locale: angular.ILocaleService
        ) {}

        // Return Currency Symbol
        getCurrencySymbol(): string {
            return this.$locale.NUMBER_FORMATS.CURRENCY_SYM;
        }

        // Return list of data for table
        processItem(list: any, criteria: IPreviewCriteria): Array<IItem> {
            let dataList = [];

            _.each(list, (el: any, i) => {
                let item: IItem = {
                    index: i + 1,
                    displayName: '',
                    quantity: el.quantity
                };

                // Compile item name
                switch (criteria.showFilter) {
                    case ShowFilterEnum.All:
                        let variantName = el.variantDisplayName ? `, ${el.variantDisplayName}` : '';

                        if (el.giftCardNumber) {
                            item.displayName = el.giftCardNumber;
                        } else if (el.membershipId) {
                            item.displayName = el.membershipDisplayName;
                        } else if (el.packageTypeId) {
                            item.displayName = el.packageDisplayName;
                        } else if (el.serviceId) {
                            item.displayName = `${el.serviceShortCut}: ${el.serviceDisplayName}`;
                        } else if (el.productId) {
                            item.displayName = `${el.productSku}: ${el.productDisplayName}${variantName}`;
                        }
                        break;
                    case ShowFilterEnum.ClientsPurchased:
                        if (el.clientId) {
                            item.displayName = `${el.clientLastName}, ${el.clientFirstName}`;
                            item.id = el.clientId;
                        } else {
                            item.displayName = this.$translate.instant('LABEL_SALES_RANKINGS_ANONYMOUS');
                        }
                        break;
                    case ShowFilterEnum.EmployeesSold:
                        item.displayName = el.employeeId && (typeof el.employeeCode !== 'undefined') ? `${el.employeeCode}: ${el.employeeLastName}, ${el.employeeFirstName}` : this.$translate.instant('LABEL_SALES_RANKINGS_HOUSE');
                         if (el.membershipId) {
                            item.itemName = el.membershipDisplayName;
                        } else if (el.packageTypeId) {
                             item.itemName = el.packageDisplayName;
                        } else if (el.serviceId) {
                             item.itemName = `${el.serviceShortCut}: ${el.serviceDisplayName}`;
                        } else if (el.productId) {
                             item.itemName = `${el.productSku}: ${el.productDisplayName}` +
                                (el.variantDisplayName ? ` - ${el.variantDisplayName}` : '');
                        }
                        break;
                }

                // Compile item amount
                switch (criteria.itemFilter) {
                    case ItemFilterEnum.GiftCards:
                    case ItemFilterEnum.Memberships:
                    case ItemFilterEnum.Packages:
                        item.value = el.amount;
                        break;
                    case ItemFilterEnum.Products:
                        item.productValue = el.amount;
                        break;
                    case ItemFilterEnum.Services:
                        item.serviceValue = el.amount;
                        break;
                    case ItemFilterEnum.ProductsServices:
                        if (criteria.showFilter === ShowFilterEnum.All) {
                            item.productValue = el.productId && el.productAmount;
                            item.serviceValue = el.serviceId && el.serviceAmount;
                        } else {
                            item.productValue = el.productAmount;
                            item.serviceValue = el.serviceAmount;
                        }

                        item.sumValue = el.amount;
                        break;
                    case ItemFilterEnum.AverageTicket:
                        item.value = el.amount.toFixed(2);
                        break;
                    case ItemFilterEnum.RetailToService:
                        item.productValue = el.productAmount;
                        item.serviceValue = el.serviceAmount;
                        item.value = +(el.amount * 100).toFixed(2);
                        break;
                    case ItemFilterEnum.RetailToTotal:
                        item.productValue = el.productAmount;
                        item.serviceValue = el.serviceAmount;
                        item.sumValue = el.totalAmount;
                        item.value = +(el.amount * 100).toFixed(2);
                        break;
                    case ItemFilterEnum.Rebooking:
                        item.sumValue = el.totalAmount;
                        item.value = +(el.amount * 100).toFixed(2);
                        break;
                }

                // Full Client Info Block for CSV
                if (criteria.showFilter === ShowFilterEnum.ClientsPurchased) {
                    item.numericIdentifier = el.numericIdentifier || '';
                    item.prefix = el.prefix || '';
                    item.firstName = el.clientFirstName || this.$translate.instant('LABEL_SALES_RANKINGS_ANONYMOUS');
                    item.middleName = el.clientMiddleName || '';
                    item.lastName = el.clientLastName || '';
                    item.address1 = el.clientAddress1 || '';
                    item.address2 = el.clientAddress2 || '';
                    item.city = el.clientCity || '';
                    item.state = el.clientState || '';
                    item.zip = el.clientZipCode || '';
                    item.homePhoneNumber = el.homePhoneNumber || '';
                    item.mobilePhoneNumber = el.mobilePhoneNumber || '';
                    item.apptNotifOptInState = el.textOptInState || '';
                    item.useForTextMarketing = el.useForTextMarketing || '';
                    item.workPhoneNumber = el.workPhoneNumber || '';
                    item.workPhoneExtension = el.workPhoneExtension || '';
                    item.primaryPhoneType = el.primaryPhoneType || '';
                    item.emailAddress = el.clientEmailAddress || '';
                    item.emailStatus = el.emailStatus || '';
                    item.emailCommOptedInState = el.emailCommOptedInState || '';
                    item.isMarketingEmailEnabled = typeof el.isMarketingEmailEnabled == 'boolean' ? el.isMarketingEmailEnabled : '';
                    item.firstVisitDate = el.firstVisitDate || '';
                    item.lastVisitDate = el.lastVisitDate || '';
                    item.gender = el.gender || '';
                    item.birthYear = el.birthYear || '';
                    item.birthMonth = el.birthMonth || '';
                    item.birthDay = el.birthDay || '';
                    item.anniversaryDate = el.anniversaryDate || '';
                    item.clientType = el.clientType || '';
                    item.isMinor = typeof el.isMinor == 'boolean' ? el.isMinor : '';
                    item.isRestricted = typeof el.isRestricted == 'boolean' ? el.isRestricted : '';
                    item.isTransient = typeof el.isTransient == 'boolean' ? el.isTransient : '';
                    item.allowBookingRebookingOnline = typeof el.allowBookingRebookingOnline == 'boolean' ? el.allowBookingRebookingOnline : '';
                    item.excludeFromReceivingPoints = typeof el.excludeFromReceivingPoints == 'boolean' ? el.excludeFromReceivingPoints : '';
                    item.allowChargesCreditOnAccount = typeof el.allowChargesCreditOnAccount == 'boolean' ? el.allowChargesCreditOnAccount : '';
                    item.isMember = typeof el.isMember == 'boolean' ? el.isMember : '';
                    item.referralType = el.referralType || '';
                    item.referralTypeDisplayName = el.referralTypeDisplayName || '';
                    item.referralFirstName = el.referralFirstName || '';
                    item.referralLastName = el.referralLastName || '';
                    item.noSellSharePersonalInfo = `${!!el.noSellSharePersonalInfo}`;

                } else if (criteria.showFilter === ShowFilterEnum.EmployeesSold) {
                    let houseEmployee = this.$translate.instant('LABEL_HOUSE');

                    item.firstName = el.employeeFirstName || (el.employeeId ? '' : houseEmployee);
                    item.lastName = el.employeeLastName || '';
                    item.employeeCode = el.employeeCode || (el.employeeId ? '' : houseEmployee.toUpperCase());
                    item.employeeId = el.employeeId || null;
                }

                dataList.push(item);

            });

            return dataList;
        }

        quoteCsvValue(value: any): string {
            if (value === null || value === undefined) {
                return "";
            }
            let val = `${value}`;
            const hasQuote = val.includes('"');
            const shouldQuote = (
                hasQuote ||
                val.includes(",") ||
                val.includes("\n") ||
                val.includes("\r")
            );
            if (hasQuote) {
                val = val.replaceAll('"', '""');
            }
            if (shouldQuote) {
                val = `"${val}"`;
            }
            return val;
        }

        // Process Row for CSV File
        processCSVRow(row: IItem, criteria: IPreviewCriteria): string {
            const finalRow = [];

            switch (criteria.showFilter) {
                case ShowFilterEnum.EmployeesSold:
                    finalRow.push(
                        this.quoteCsvValue(row.firstName),
                        this.quoteCsvValue(row.lastName),
                        this.quoteCsvValue(row.employeeCode)
                    );

                    switch (criteria.itemFilter) {
                        case ItemFilterEnum.Memberships:
                        case ItemFilterEnum.Packages:
                        case ItemFilterEnum.Products:
                        case ItemFilterEnum.Services:
                            finalRow.push(
                                this.quoteCsvValue(row.itemName)
                            );
                            break;
                    }

                    break;
                case ShowFilterEnum.All:
                    finalRow.push(
                        this.quoteCsvValue(row.displayName)
                    );
                    break;
            }

            if (criteria.itemFilter !== ItemFilterEnum.RetailToService
                && criteria.itemFilter !== ItemFilterEnum.RetailToTotal
                && criteria.itemFilter !== ItemFilterEnum.Rebooking
                && criteria.itemFilter !== ItemFilterEnum.AverageTicket) {
                finalRow.push(
                    this.quoteCsvValue(row.quantity)
                );
            }

            switch (criteria.itemFilter) {
                case ItemFilterEnum.GiftCards:
                case ItemFilterEnum.Memberships:
                case ItemFilterEnum.Packages:
                case ItemFilterEnum.AverageTicket:
                    finalRow.push(
                        this.quoteCsvValue(row.value)
                    );
                    break;
                case ItemFilterEnum.Products:
                    finalRow.push(
                        this.quoteCsvValue(row.productValue)
                    );
                    break;
                case ItemFilterEnum.Services:
                    finalRow.push(
                        this.quoteCsvValue(row.serviceValue)
                    );
                    break;
                case ItemFilterEnum.ProductsServices:
                    finalRow.push(
                        this.quoteCsvValue(row.productValue),
                        this.quoteCsvValue(row.serviceValue),
                        this.quoteCsvValue(row.sumValue)
                    );
                    break;
                case ItemFilterEnum.RetailToService:
                    finalRow.push(
                        this.quoteCsvValue(row.productValue),
                        this.quoteCsvValue(row.serviceValue),
                        this.quoteCsvValue(+(row.value) / 100)
                    );
                    break;
                case ItemFilterEnum.RetailToTotal:
                    finalRow.push(
                        this.quoteCsvValue(row.productValue),
                        this.quoteCsvValue(row.serviceValue),
                        this.quoteCsvValue(row.sumValue),
                        this.quoteCsvValue(+(row.value) / 100)
                    );
                    break;
                case ItemFilterEnum.Rebooking:
                    finalRow.push(
                        this.quoteCsvValue(row.quantity),
                        this.quoteCsvValue(row.sumValue),
                        this.quoteCsvValue(+(row.value) / 100)
                    );
                    break;
            }

            if (criteria.showFilter === ShowFilterEnum.ClientsPurchased) {
                finalRow.push(
                    this.quoteCsvValue(row.numericIdentifier),
                    this.quoteCsvValue(row.prefix),
                    this.quoteCsvValue(row.firstName),
                    this.quoteCsvValue(row.middleName),
                    this.quoteCsvValue(row.lastName),
                    this.quoteCsvValue(row.address1),
                    this.quoteCsvValue(row.address2),
                    this.quoteCsvValue(row.city),
                    this.quoteCsvValue(row.state),
                    this.quoteCsvValue(row.zip),
                    this.quoteCsvValue(row.homePhoneNumber),
                    this.quoteCsvValue(row.mobilePhoneNumber),
                    this.quoteCsvValue(row.apptNotifOptInState),
                    this.quoteCsvValue(row.useForTextMarketing),
                    this.quoteCsvValue(row.workPhoneNumber),
                    this.quoteCsvValue(row.workPhoneExtension),
                    this.quoteCsvValue(row.primaryPhoneType),
                    this.quoteCsvValue(row.emailAddress),
                    this.quoteCsvValue(row.emailStatus),
                    this.quoteCsvValue(row.emailCommOptedInState),
                    this.quoteCsvValue(row.isMarketingEmailEnabled),
                    this.quoteCsvValue(row.firstVisitDate),
                    this.quoteCsvValue(row.lastVisitDate),
                    this.quoteCsvValue(row.gender),
                    this.quoteCsvValue(row.birthYear),
                    this.quoteCsvValue(row.birthMonth),
                    this.quoteCsvValue(row.birthDay),
                    this.quoteCsvValue(row.anniversaryDate),
                    this.quoteCsvValue(row.clientType),
                    this.quoteCsvValue(row.isMinor),
                    this.quoteCsvValue(row.isRestricted),
                    this.quoteCsvValue(row.isTransient),
                    this.quoteCsvValue(row.allowBookingRebookingOnline),
                    this.quoteCsvValue(row.excludeFromReceivingPoints),
                    this.quoteCsvValue(row.allowChargesCreditOnAccount),
                    this.quoteCsvValue(row.isMember),
                    this.quoteCsvValue(row.referralType),
                    this.quoteCsvValue(row.referralTypeDisplayName),
                    this.quoteCsvValue(row.referralFirstName),
                    this.quoteCsvValue(row.referralLastName),
                    this.quoteCsvValue(row.noSellSharePersonalInfo)
                );
            }

            return finalRow.join(",") + '\n';
        }

        // Compile Filter Type Enum
        compileFilterType(criteria: IPreviewCriteria): number {
            let filterEnum;

            if (criteria.showFilter === ShowFilterEnum.All) {
                switch (criteria.itemFilter) {
                    case ItemFilterEnum.GiftCards:
                        filterEnum = SaleRankFilterTypeEnum.GiftCardsSold;
                        break;
                    case ItemFilterEnum.Memberships:
                        filterEnum = SaleRankFilterTypeEnum.MembershipsSold;
                        break;
                    case ItemFilterEnum.Packages:
                        filterEnum = SaleRankFilterTypeEnum.PackagesSold;
                        break;
                    case ItemFilterEnum.Products:
                        filterEnum = SaleRankFilterTypeEnum.ProductsSold;
                        break;
                    case ItemFilterEnum.Services:
                        filterEnum = SaleRankFilterTypeEnum.ServicesSold;
                        break;
                    case ItemFilterEnum.ProductsServices:
                        filterEnum = SaleRankFilterTypeEnum.ProductsOrServicesSold;
                        break;
                }
            } else if (criteria.showFilter === ShowFilterEnum.ClientsPurchased) {
                switch (criteria.itemFilter) {
                    case ItemFilterEnum.GiftCards:
                        filterEnum = SaleRankFilterTypeEnum.ClientsBuyingGiftCards;
                        break;
                    case ItemFilterEnum.Memberships:
                        filterEnum = SaleRankFilterTypeEnum.ClientsBuyingMemberships;
                        break;
                    case ItemFilterEnum.Packages:
                        filterEnum = SaleRankFilterTypeEnum.ClientsBuyingPackages;
                        break;
                    case ItemFilterEnum.Products:
                        filterEnum = SaleRankFilterTypeEnum.ClientsBuyingProducts;
                        break;
                    case ItemFilterEnum.Services:
                        filterEnum = SaleRankFilterTypeEnum.ClientsBuyingServices;
                        break;
                    case ItemFilterEnum.ProductsServices:
                        filterEnum = SaleRankFilterTypeEnum.ClientsBuyingProductsOrServices;
                        break;
                }
            } else if (criteria.showFilter === ShowFilterEnum.EmployeesSold) {
                switch (criteria.itemFilter) {
                    case ItemFilterEnum.GiftCards:
                        filterEnum = SaleRankFilterTypeEnum.EmployeesSellingGiftCards;
                        break;
                    case ItemFilterEnum.Memberships:
                        filterEnum = SaleRankFilterTypeEnum.EmployeesSellingMemberships;
                        break;
                    case ItemFilterEnum.Packages:
                        filterEnum = SaleRankFilterTypeEnum.EmployeesSellingPackages;
                        break;
                    case ItemFilterEnum.Products:
                        filterEnum = SaleRankFilterTypeEnum.EmployeesSellingProducts;
                        break;
                    case ItemFilterEnum.Services:
                        filterEnum = SaleRankFilterTypeEnum.EmployeesSellingServices;
                        break;
                    case ItemFilterEnum.ProductsServices:
                        filterEnum = SaleRankFilterTypeEnum.EmployeesSellingProductsOrServices;
                        break;
                    case ItemFilterEnum.AverageTicket:
                        filterEnum = SaleRankFilterTypeEnum.AverageTicket;
                        break;
                    case ItemFilterEnum.Rebooking:
                        filterEnum = SaleRankFilterTypeEnum.Rebooking;
                        break;
                    case ItemFilterEnum.RetailToService:
                        filterEnum = SaleRankFilterTypeEnum.RetailToService;
                        break;
                    case ItemFilterEnum.RetailToTotal:
                        filterEnum = SaleRankFilterTypeEnum.RetailToTotal;
                        break;
                }
            }

            return filterEnum;
        }

        // Parameters for table columns and titles
        getTableView(criteria: IPreviewCriteria): ITableView {
            let tableColumns = {
                    others: false,
                    products: false,
                    services: false,
                    productsServices: false,
                    retailToService: false,
                    retailToTotal: false,
                    rebooking: false
                },
                currencySymbol = this.getCurrencySymbol(),
                tableTitle,
                totalTitle = this.$translate.instant('LABEL_SALES_RANKINGS_TABLE_TOTAL', { currencySymbol: currencySymbol }),
                productTitle = this.$translate.instant('LABEL_SALES_RANKINGS_TABLE_PRODUCT', { currencySymbol: currencySymbol }),
                serviceTitle = this.$translate.instant('LABEL_SALES_RANKINGS_TABLE_SERVICE', { currencySymbol: currencySymbol }),
                giftCardsTitle = this.$translate.instant('LABEL_SALES_RANKINGS_TABLE_GIFTCARDS', { currencySymbol: currencySymbol }),
                membershipsTitle = this.$translate.instant('LABEL_SALES_RANKINGS_TABLE_MEMBERSHIPS', { currencySymbol: currencySymbol }),
                packagesTitle = this.$translate.instant('LABEL_SALES_RANKINGS_TABLE_PACKAGES', { currencySymbol: currencySymbol }),
                productServiceTitle = this.$translate.instant('LABEL_SALES_RANKINGS_TABLE_PRODUCT_SERVICE', { currencySymbol: currencySymbol }),
                averageTicketTitle = this.$translate.instant('LABEL_SALES_RANKINGS_TABLE_AVERAGE_TICKET', { currencySymbol: currencySymbol }),
                retailToServiceTitle = this.$translate.instant('LABEL_RETAIL_TO_SERVICE_PERCENT'),
                retailToTotalTitle = this.$translate.instant('LABEL_RETAIL_TO_TOTAL'),
                clientsRebookedTitle = this.$translate.instant('LABEL_CLIENTS_REBOOKED'),
                rebookedTitle = this.$translate.instant('LABEL_REBOOKED_PERCENT');

            switch (criteria.itemFilter) {
                case ItemFilterEnum.GiftCards:
                    tableColumns.others = true;
                    tableTitle = criteria.showFilter === ShowFilterEnum.All ? totalTitle : giftCardsTitle;
                    break;
                case ItemFilterEnum.Memberships:
                    tableColumns.others = true;
                    tableTitle = criteria.showFilter === ShowFilterEnum.All ? totalTitle : membershipsTitle;
                    break;
                case ItemFilterEnum.Packages:
                    tableColumns.others = true;
                    tableTitle = criteria.showFilter === ShowFilterEnum.All ? totalTitle : packagesTitle;
                    break;
                case ItemFilterEnum.Products:
                    tableColumns.products = true;
                    productTitle = criteria.showFilter === ShowFilterEnum.All ? totalTitle : productTitle;
                    break;
                case ItemFilterEnum.Services:
                    tableColumns.services = true;
                    serviceTitle = criteria.showFilter === ShowFilterEnum.All ? totalTitle : serviceTitle;
                    break;
                case ItemFilterEnum.ProductsServices:
                    tableColumns.products = true;
                    tableColumns.services = true;
                    tableColumns.productsServices = criteria.showFilter !== ShowFilterEnum.All;
                    break;
                case ItemFilterEnum.AverageTicket:
                    tableColumns.others = true;
                    tableTitle = averageTicketTitle;
                    break;
                case ItemFilterEnum.RetailToService:
                    tableColumns.products = true;
                    tableColumns.services = true;
                    tableColumns.retailToService = true;
                    tableTitle = retailToServiceTitle;
                    break;
                case ItemFilterEnum.RetailToTotal:
                    tableColumns.products = true;
                    tableColumns.services = true;
                    tableColumns.retailToTotal = true;
                    tableTitle = totalTitle;
                    break;
                case ItemFilterEnum.Rebooking:
                    tableColumns.rebooking = true;
                    break;
            }

            return {
                tableColumns: tableColumns,
                tableTitle: tableTitle,
                productTitle: productTitle,
                serviceTitle: serviceTitle,
                productServiceTitle: productServiceTitle,
                retailToServiceTitle: retailToServiceTitle,
                retailToTotalTitle: retailToTotalTitle,
                clientsRebookedTitle: clientsRebookedTitle,
                rebookedTitle: rebookedTitle
            }
        }

        // Return detailed list of items which Employee Sold
        detailedItemsList(list: any): Array<any> {
            let detailedList = [];

            _.each(list, (e: any) => {
                    let detail;

                    if (e.productId) {
                        let variant = "";
                        if (e.variantDisplayName) {
                            variant = `, ${e.variantDisplayName}`;
                        }
                        detail = {
                            name: `${e.productSku}: ${e.productDisplayName}${variant}`,
                            type: SaleRankDetailType.Products
                        }
                    }

                    if (e.serviceId) {
                        detail = {
                            name: `${e.serviceShortCut}: ${e.serviceDisplayName}`,
                            type: SaleRankDetailType.Services
                        }
                    }

                    if (e.giftCardNumber) {
                        detail = {
                            name: e.giftCardNumber,
                            type: SaleRankDetailType.GiftCards
                        }
                    }

                    if (e.membershipId) {
                        detail = {
                            name: e.membershipDisplayName,
                            type: SaleRankDetailType.Memberships
                        }
                    }

                    if (e.packageTypeId) {
                        detail = {
                            name: e.packageDisplayName,
                            type: SaleRankDetailType.Packages
                        }
                    }

                    detail.quantity = e.quantity;
                    detail.amount = e.amount;

                    detailedList.push(detail);

            });

            return detailedList;
        }
    }

    angular.module('meevo.plugins.salesRankings')
        .service('meevo.plugins.salesRankings.SalesRankingsService', SalesRankingsService);
}
